{
  "title": "Configuración de la empresa",
  "subtitle": "Reglas operativas que aplican a todas las sedes y marcadores.",
  "sections": {
    "attendance": "Asistencia",
    "retention": "Retención y privacidad",
    "autonomous": "Modo autónomo del marcador",
    "vpp": "Marcación con el marcador",
    "vppHelp": "Cómo se comporta el marcador cuando varias personas marcan seguidas, y cuánta imprecisión de ubicación se tolera."
  },
  "company": {
    "title": "Datos de la empresa",
    "legalName": "Razón social",
    "tradeName": "Nombre comercial",
    "taxId": "Identificador fiscal",
    "country": "País",
    "timezone": "Zona horaria por defecto",
    "status": "Estado",
    "statusValue": {
      "ACTIVE": "Activa",
      "SUSPENDED": "Suspendida"
    }
  },
  "fields": {
    "max_open_shift_hours": "Jornada abierta máxima",
    "marker_shows_name": "Nombre en el marcador",
    "location_policy": "Política de ubicación",
    "attendance_retention_years": "Conservar asistencia",
    "location_retention_days": "Conservar ubicaciones",
    "anonymize_after_months": "Anonimizar después de",
    "autonomous_mode_enabled": "Activar el modo autónomo",
    "autonomous_mode_after_seconds": "Entrar en modo autónomo tras",
    "autonomous_marks_per_worker_interval_s": "Intervalo entre marcas autónomas",
    "vpp_queue_max_size": "Personas en espera",
    "vpp_queue_max_wait_ms": "Espera máxima en la fila",
    "vpp_marker_confirmation_ms": "Confirmación en pantalla",
    "vpp_marker_confirmation_busy_ms": "Confirmación con fila",
    "geo_low_accuracy_threshold_m": "Ubicación poco precisa desde",
    "regularization_window_days": "Plazo para regularizar (días)",
    "late_tolerance_minutes": "Tolerancia de tardanza (min)",
    "early_leave_tolerance_minutes": "Tolerancia de salida anticipada (min)",
    "shift_match_window_minutes": "Ventana para asociar marcas a un turno (min)",
    "schedule_horizon_weeks": "Semanas generadas por los patrones",
    "timesheet_layout": "Formato del tareo"
  },
  "help": {
    "max_open_shift_hours": "horas, entre 4 y 48. Pasado ese tiempo la jornada se marca como abierta demasiado tiempo.",
    "marker_shows_name": "Qué se muestra en la pantalla del marcador al reconocer a la persona.",
    "location_policy": "«Señalar» registra la ubicación y avisa si está fuera del radio; «Ignorar» no la usa.",
    "attendance_retention_years": "años, entre 5 y 10.",
    "location_retention_days": "días, entre 30 y 365.",
    "anonymize_after_months": "meses, entre 6 y 60.",
    "autonomous_mode_enabled": "El marcador sigue registrando marcas aunque pierda conexión.",
    "autonomous_mode_after_seconds": "segundos sin conexión, entre 10 y 120.",
    "autonomous_marks_per_worker_interval_s": "segundos entre dos marcas de la misma persona, entre 30 y 600.",
    "vpp_queue_max_size": "personas, entre 1 y 10. Pasado ese número, el marcador pide esperar un momento.",
    "vpp_queue_max_wait_ms": "milisegundos, entre 5000 y 30000. Es lo máximo que alguien espera su turno antes de reintentar.",
    "vpp_marker_confirmation_ms": "milisegundos, entre 500 y 2000. Cuánto se queda el saludo en la pantalla del marcador.",
    "vpp_marker_confirmation_busy_ms": "milisegundos, entre 300 y 1500. Lo mismo, pero cuando hay alguien esperando detrás.",
    "geo_low_accuracy_threshold_m": "metros, entre 30 y 500. Por encima de ese margen la ubicación se considera poco precisa.",
    "regularization_window_days": "Días hacia atrás en los que el trabajador puede proponer la hora que le faltó marcar. Entre 1 y 30.",
    "late_tolerance_minutes": "minutos, entre 0 y 60. Una entrada dentro de ese margen cuenta a tiempo; si lo supera, la tardanza se cuenta desde el inicio del turno. Cada turno puede tener la suya.",
    "early_leave_tolerance_minutes": "minutos, entre 0 y 60. Una salida dentro de ese margen antes del fin del turno no cuenta como salida anticipada.",
    "shift_match_window_minutes": "minutos, entre 60 y 480. Una marca que cae hasta ese tiempo antes o después de un turno se asocia a ese turno.",
    "schedule_horizon_weeks": "semanas, entre {min} y {max}. Los patrones de turnos se generan hasta ese número de semanas hacia adelante.",
    "schedule_horizon_equivalent": "Equivale a {days} días.",
    "timesheet_layout": "Hojas y columnas del Excel del tareo que se descarga."
  },
  "options": {
    "markerShowsName": {
      "FIRST_NAME": "Solo el nombre",
      "INITIALS": "Iniciales",
      "NONE": "No mostrar el nombre"
    },
    "locationPolicy": {
      "SIGNAL": "Señalar cuando esté fuera del radio",
      "IGNORE": "Ignorar la ubicación"
    },
    "timesheetLayout": {
      "STANDARD": "Estándar (Tareo, Detalle y Leyenda)"
    }
  },
  "errors": {
    "range": "El valor está fuera del rango permitido."
  },
  "forbidden": {
    "title": "No tienes acceso a esta pantalla",
    "description": "Solo el administrador de la empresa puede cambiar su configuración."
  },
  "error": {
    "title": "No pudimos cargar la empresa"
  },
  "toast": {
    "saved": "Configuración guardada"
  },
  "effective": {
    "autonomousMatches": "Valor vigente: {seconds} segundos.",
    "autonomousOverridden": "Valor vigente: {seconds} segundos. Guardaste {configured}, pero esta instalación necesita ese tiempo antes de dar el marcador por desconectado: si entrara antes, nadie podría marcar en esos segundos."
  },
  "retention": {
    "description": "Cuánto tiempo guarda Fermin la asistencia, las ubicaciones exactas y los datos personales de quienes ya no trabajan en la empresa. Los procesos corren solos todos los días a las {time} (hora de {timezone}).",
    "processes": {
      "attendance": "Asistencia: las marcaciones y su registro de auditoría se conservan al menos este tiempo.",
      "locations": "Ubicaciones: cada día a las {time} (hora de {timezone}) se borran la latitud, la longitud y la precisión de las marcas más antiguas que este plazo. Se conservan el estado de la ubicación y las observaciones.",
      "anonymize": "Anonimización: cada día a las {time} (hora de {timezone}) se reemplazan el nombre, el documento, el teléfono y el correo de quienes llevan dados de baja más que este plazo. Sus marcaciones se conservan con un identificador anónimo y ya no se pueden editar ni reactivar."
    },
    "confirm": {
      "title": "¿Cambiar la política de retención?",
      "description": "La política de retención y privacidad de la empresa quedará así:",
      "attendance": "Conservar la asistencia {years} años.",
      "locations": "Borrar las ubicaciones exactas de las marcas pasados {days} días.",
      "anonymize": "Anonimizar a los trabajadores {months} meses después de su baja.",
      "warning": "El borrado de ubicaciones y la anonimización no se pueden deshacer. Se aplican en el próximo proceso diario.",
      "action": "Cambiar la política"
    }
  }
}
